'use client'

import { useAuthorization } from '@entities/authentication'
import { subscribesTicketEntityQueries } from '@entities/subscribes/api'
import { SubscribeTicketCard, SubscribeTicketLanguageChip } from '@entities/subscribes/ui'
import { langTypeSchema, lessonTimeSchema } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { useSuspenseQuery } from '@tanstack/react-query'
import Image from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'
import z from 'zod'

import { Box, Flex, HStack, Typography, VStack } from '@podo-app/design-system-temp'

import { LECTURE_TICKETS_QUERY_PARAMS } from '../../model'

type Props = {
  langType: z.infer<typeof langTypeSchema>
  lessonTime: z.infer<typeof lessonTimeSchema>
}

export const IpadLectureTickets = ({ langType, lessonTime }: Props) => {
  const t = useTranslation()
  const { bearerToken } = useAuthorization()
  const searchParams = useSearchParams()
  const router = useRouter()
  const { data: subscribeTicketsData } = useSuspenseQuery({
    ...subscribesTicketEntityQueries.ipadList({
      bearerToken,
      langType,
      lessonTime: lessonTime.toString() as NumberEnumToStringEnum<z.infer<typeof lessonTimeSchema>>,
    }),
  })

  const subscribeTickets = Array.isArray(subscribeTicketsData) ? subscribeTicketsData : []

  return (
    <VStack className="gap-8">
      <VStack className="gap-5">
        <VStack className="gap-2">
          <Typography size="h1" className="text-[28px] font-bold">
            {t('subscribeList.paymentTypeHero.ipadTitle')}
          </Typography>
          <Typography size="h4" className="text-[18px]">
            {t('subscribeList.paymentTypeHero.ipadLine1')} <br />
            {t('subscribeList.paymentTypeHero.ipadLine2')}
          </Typography>
        </VStack>

        <HStack className="gap-2.5">
          <Box className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
            <Typography size="body1">{t('subscribeList.paymentTypeHero.unlimitedTicket')}</Typography>
          </Box>
          <Box className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
            <Typography size="body1">iPad</Typography>
          </Box>
          <Box className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
            <Typography size="body1">Apple Pencil</Typography>
          </Box>
        </HStack>

        <Flex className="relative w-full h-[335px]">
          <Image
            src="/images/ipad-package-banner.png"
            alt={t('subscribeList.paymentTypeHero.ipadBannerAlt')}
            fill
            sizes="(max-width: 768px) 540px, 100vw"
            className="object-contain"
          />
        </Flex>
      </VStack>

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.language')}</Typography>

        <SubscribeTicketLanguageChip
          selectedLanguage={langType}
          handleChangeLanguage={(language) => {
            const queryParams = new URLSearchParams(searchParams.toString())
            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.langType, language)
            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
        />
      </VStack>
      {/* NOTE: 레슨 시간 추가 시 활성화 */}
      {/*
      <VStack className="gap-4">
        <Typography size="h3">레슨 시간을 선택하세요.</Typography>

        <SubscribeTicketLessonTimeChip
          selectedLessonTime={transformNumberEnumToStringEnum(lessonTime)}
          handleChangeLessonTime={(selectedLessonTime) => {
            const queryParams = new URLSearchParams(searchParams.toString())
            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.lessonTime, selectedLessonTime)
            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
        />
      </VStack> */}

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.product')}</Typography>

        {subscribeTickets.map((subscribeTicket) => (
          <SubscribeTicketCard
            month={subscribeTicket.lessonMonth}
            key={subscribeTicket.id}
            subscribeTicketId={subscribeTicket.id}
            catalogTicket={subscribeTicket}
            price={subscribeTicket.originPrice}
            descriptions={[
              t('subscribeList.ticketDescriptions.ipadPackage', { months: subscribeTicket.lessonMonth }),
              t('subscribeList.paymentTypeHero.ipadProductIpad'),
              t('subscribeList.paymentTypeHero.ipadProductPencil'),
            ]}
            isBest={subscribeTicket.isBest}
          />
        ))}
      </VStack>
    </VStack>
  )
}
